// Org Chart — one coloured block per team: description, lead(s) on top, people with faces and their other teams.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Network, X } from "lucide-react";
import { workspaceApi, isMgmt, userKey } from "./_shared";
import { useAuth } from "../../context/AuthContext";
import OrgTeamCard from "./panels/OrgTeamCard";
import OrgPersonPopover from "./panels/OrgPersonPopover";
import TeamCard, { useSquadFaces } from "./panels/TeamCard";
import "./workspace.css";

export default function OrgChart() {
  const { user } = useAuth();
  const mgmt = isMgmt(user);
  const faces = useSquadFaces();
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [person, setPerson] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { setChart(await workspaceApi.orgChart()); }
    catch { toast.error("Failed to load org chart"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const nodeById = useMemo(() => Object.fromEntries((chart?.nodes || []).map((n) => [n.id, n])), [chart]);

  if (loading) return <div className="text-slate-500 py-12 text-center" data-testid="ws-org-loading">Loading org chart…</div>;
  if (!chart) return null;
  const { teams, nodes } = chart;
  const multi = nodes.filter((n) => teams.filter((t) => (t.member_ids || []).includes(n.id)).length > 1).length;
  const me = userKey(user);
  const editTeam = editing && teams.find((t) => t.id === editing);

  return (
    <div className="space-y-4" data-testid="workspace-org-chart">
      <div className="flex items-center gap-2 flex-wrap">
        <Network className="w-4 h-4 text-emerald-300" />
        <div className="text-[13px] font-semibold text-white">Organisation</div>
        <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest" data-testid="ws-org-summary">
          {teams.length} teams · {nodes.length} people · {multi} wear several hats
        </div>
        {!mgmt && <div className="ml-auto text-[10px] font-mono text-slate-500 uppercase tracking-widest">View only</div>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 items-start" data-testid="ws-org-team-grid">
        {teams.map((t) => (
          <OrgTeamCard key={t.id} team={t} teams={teams} nodeById={nodeById} faces={faces}
            canEdit={mgmt || (t.member_ids || []).includes(me)} canLead={mgmt} onOpen={setPerson} onEdit={(x) => setEditing(x.id)} onSaved={load} />
        ))}
      </div>
      {editTeam && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setEditing(null)} data-testid="ws-org-edit-modal">
          <div className="w-full max-w-3xl relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setEditing(null)} className="absolute -top-8 right-0 text-slate-300 hover:text-white" data-testid="ws-org-edit-close"><X className="w-5 h-5" /></button>
            <div className="bg-[#0F1420] rounded-xl">
              <TeamCard team={editTeam} users={nodes} canEdit canEditAll={mgmt} startEditing onSaved={() => { setEditing(null); load(); }} />
            </div>
          </div>
        </div>
      )}
      {person && <OrgPersonPopover node={nodeById[person]} teams={teams} managers={nodes.filter((n) => n.is_manager)} isEditor={mgmt} onClose={() => setPerson(null)} onSaved={load} />}
    </div>
  );
}
